import React, { useState, useEffect, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ApplicationOverview = ({ applications = [] }) => {
  const [selectedPeriod, setSelectedPeriod] = useState("thisWeek"); // Default to 'thisWeek'

  // Process applications data based on selected period
  const processChartData = useMemo(() => {
    const now = new Date();
    let labels = [];
    let applicationsData = [];
    let approvedData = [];
    let rejectedData = [];

    // Filter applications based on selected period
    const filteredApplications = applications.filter((app) => {
      const appDate = new Date(app.createdAt);
      const isIncluded = (() => {
        switch (selectedPeriod) {
          case "today":
            return appDate.toDateString() === now.toDateString();
          case "thisWeek":
            const weekStart = new Date(
              now.setDate(now.getDate() - now.getDay())
            );
            const weekEnd = new Date(
              now.setDate(now.getDate() - now.getDay() + 6)
            );
            return appDate >= weekStart && appDate <= weekEnd;
          case "thisMonth":
            return (
              appDate.getMonth() === now.getMonth() &&
              appDate.getFullYear() === now.getFullYear()
            );
          case "thisYear":
            return appDate.getFullYear() === now.getFullYear();
          case "allTime":
            return true;
          default:
            return true;
        }
      })();

      return isIncluded;
    });

    // Log filtered applications summary

    // Generate labels and data based on period
    switch (selectedPeriod) {
      case "today":
        labels = ["Morning", "Afternoon", "Evening"];
        applicationsData = [0, 0, 0];
        approvedData = [0, 0, 0];
        rejectedData = [0, 0, 0];

        filteredApplications.forEach((app) => {
          const hour = new Date(app.createdAt).getHours();
          const index = hour < 12 ? 0 : hour < 18 ? 1 : 2;
          applicationsData[index]++;
          if (app.status.toLowerCase() === "approved") {
            approvedData[index]++;
          } else if (app.status.toLowerCase() === "rejected") {
            rejectedData[index]++;
          }
        });
        break;
      case "thisWeek":
        labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
        applicationsData = Array(7).fill(0);
        approvedData = Array(7).fill(0);
        rejectedData = Array(7).fill(0);

        filteredApplications.forEach((app) => {
          const day = new Date(app.createdAt).getDay();
          applicationsData[day]++;
          if (app.status.toLowerCase() === "approved") {
            approvedData[day]++;
          } else if (app.status.toLowerCase() === "rejected") {
            rejectedData[day]++;
          }
        });
        break;

      case "thisMonth":
        labels = ["Week 1", "Week 2", "Week 3", "Week 4"];
        applicationsData = Array(4).fill(0);
        approvedData = Array(4).fill(0);
        rejectedData = Array(4).fill(0);

        filteredApplications.forEach((app) => {
          const date = new Date(app.createdAt);
          const week = Math.floor((date.getDate() - 1) / 7);
          applicationsData[week]++;
          if (app.status.toLowerCase() === "approved") {
            approvedData[week]++;
          } else if (app.status.toLowerCase() === "rejected") {
            rejectedData[week]++;
          }
        });
        break;

      case "thisYear":
        labels = ["Q1", "Q2", "Q3", "Q4"];
        applicationsData = Array(4).fill(0);
        approvedData = Array(4).fill(0);
        rejectedData = Array(4).fill(0);

        filteredApplications.forEach((app) => {
          const month = new Date(app.createdAt).getMonth();
          const quarter = Math.floor(month / 3);
          applicationsData[quarter]++;
          if (app.status.toLowerCase() === "approved") {
            approvedData[quarter]++;
          } else if (app.status.toLowerCase() === "rejected") {
            rejectedData[quarter]++;
          }
        });
        break;

      case "allTime":
        const years = [
          ...new Set(
            filteredApplications.map((app) =>
              new Date(app.createdAt).getFullYear()
            )
          ),
        ].sort();
        labels = years.map((year) => year.toString());
        applicationsData = Array(years.length).fill(0);
        approvedData = Array(years.length).fill(0);
        rejectedData = Array(years.length).fill(0);

        filteredApplications.forEach((app) => {
          const year = new Date(app.createdAt).getFullYear();
          const yearIndex = years.indexOf(year);
          applicationsData[yearIndex]++;
          if (app.status.toLowerCase() === "approved") {
            approvedData[yearIndex]++;
          } else if (app.status.toLowerCase() === "rejected") {
            rejectedData[yearIndex]++;
          }
        });
        break;
    }

    return {
      labels,
      applications: applicationsData,
      approved: approvedData,
      rejected: rejectedData,
    };
  }, [applications, selectedPeriod]);

  const chartDataConfig = {
    labels: processChartData.labels,
    datasets: [
      {
        label: "Total Applications",
        data: processChartData.applications,
        borderColor: "#FFD700",
        backgroundColor: "rgba(255, 215, 0, 0.1)",
        tension: 0, // Straight lines
        pointBackgroundColor: "#FFD700",
        pointBorderColor: "#FFD700",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: false,
          color: "rgba(255, 255, 255, 0.1)",
        },
        ticks: {
          color: "#9CA3AF",
        },
      },
      y: {
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        ticks: {
          color: "#9CA3AF",
          beginAtZero: true,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#1F2937",
        titleColor: "#F3F4F6",
        bodyColor: "#F3F4F6",
        callbacks: {
          label: function (context) {
            const labelIndex = context.dataIndex;
            const lines = [];
            if (
              processChartData.applications &&
              processChartData.applications[labelIndex] !== undefined
            ) {
              lines.push(
                `Total Applications: ${processChartData.applications[labelIndex]}`
              );
            }
            if (
              processChartData.approved &&
              processChartData.approved[labelIndex] !== undefined
            ) {
              lines.push(`Approved: ${processChartData.approved[labelIndex]}`);
            }
            if (processChartData.rejected && processChartData.rejected[labelIndex] !== undefined) {
              lines.push(`Rejected: ${processChartData.rejected[labelIndex]}`);
            }
            return lines;
          },
        },
      },
    },
  };

  return (
    <div className="flex-1 bg-[#131313] rounded-lg min-h-[360px] p-4 flex flex-col border-2 border-[#333333]">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-white text-lg ">Application Overview</h3>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="bg-transparent border-2 border-[#FFD700] rounded-full pl-2 py-1 text-[#FFD700] cursor-pointer focus:outline-none text-sm"
        >
          <option className=" bg-[#131313]" value="today">
            Today
          </option>
          <option className=" bg-[#131313]" value="thisWeek">
            This Week
          </option>
          <option className=" bg-[#131313]" value="thisMonth">
            This Month
          </option>
          <option className=" bg-[#131313]" value="thisYear">
            This Year
          </option>
          <option className=" bg-[#131313]" value="allTime">
            All Time
          </option>
        </select>
      </div>

      {processChartData.labels.length > 0 ? (
        <div className="flex-1 relative mt-5 h-[250px] sm:h-[300px]">
          {/* Ensure chart has enough height */}
          <Line data={chartDataConfig} options={chartOptions} />
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-500 mt-5 h-[250px] sm:h-[300px]">
        No data available for the selected period.
        </div>
      )}
    </div>
  );
};

export default ApplicationOverview;
